<template>
  <v-container :class="$vuetify.display.mobile ? 'py-1 px-1' : 'py-4'" fluid>
    <v-row>
      <v-col cols="12">
        <!-- Header - Compact on Mobile -->
        <div :class="$vuetify.display.mobile ? 'mb-2' : 'mb-6'">
          <div v-if="!$vuetify.display.mobile" class="d-flex justify-space-between align-center">
            <div>
              <h1 class="text-h4 text-gradient">{{ $t('student.instructors.title') || 'Инструкторлар' }}</h1>
              <p class="text-h6 text-medium-emphasis mt-2">
                {{ $t('student.instructors.subtitle') || 'Сіздің инструкторыңызды таңдаңыз немесе жаңасын іздеңіз' }}
              </p>
            </div>
            
            <v-btn
              color="primary"
              size="large"
              prepend-icon="mdi-refresh"
              @click="loadInstructors"
              :loading="loading"
            >
              {{ $t('common.refresh') || 'Жаңарту' }}
            </v-btn>
          </div>
          
          <!-- Mobile Header -->
          <div v-else class="d-flex justify-space-between align-center pa-2">
            <h1 class="text-body-1 font-weight-bold text-white">{{ $t('student.instructors.title') || 'Инструкторлар' }}</h1>
            <v-btn
              color="primary"
              size="small"
              icon="mdi-refresh"
              @click="loadInstructors"
              :loading="loading"
            />
          </div>
        </div>

        <!-- Current Instructor Card (if assigned) -->
        <v-card v-if="currentInstructor" class="mb-6" color="primary" variant="tonal">
          <v-card-text>
            <div class="d-flex align-center">
              <v-avatar size="80" class="me-4">
                <v-img v-if="currentInstructor.photo" :src="getInstructorPhoto(currentInstructor.photo)" />
                <v-icon v-else size="40">mdi-account-tie</v-icon>
              </v-avatar>
              
              <div class="flex-grow-1">
                <h3 class="text-h5 mb-2">{{ $t('student.instructors.current') || 'Менің инструкторым' }}</h3>
                <div class="text-h6">{{ currentInstructor.fio }}</div>
                <div class="text-body-2 mt-1">
                  <v-icon size="small" class="me-1">mdi-car</v-icon>
                  {{ currentInstructor.carModel }}
                </div>
                <div class="d-flex align-center mt-2">
                  <v-rating
                    :model-value="currentInstructor.rating"
                    readonly
                    color="amber"
                    size="small"
                    density="compact"
                  />
                  <span class="text-body-2 ml-2">{{ currentInstructor.rating.toFixed(1) }}</span>
                  <span class="text-caption ml-1">({{ currentInstructor.totalRatings }} {{ $t('common.reviews') || 'пікір' }})</span>
                </div>
              </div>

              <div>
                <v-btn
                  v-if="features.chat"
                  color="white"
                  variant="outlined"
                  prepend-icon="mdi-message"
                  @click="openChat(currentInstructor)"
                  class="mb-2"
                  block
                >
                  {{ $t('common.message') || 'Хабарлама' }}
                </v-btn>
                <v-btn
                  color="white"
                  variant="outlined"
                  prepend-icon="mdi-calendar-plus"
                  @click="scheduleLesson(currentInstructor)"
                  block
                >
                  {{ $t('student.scheduleLesson') || 'Сабақ жазылу' }}
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Search and Filters -->
        <v-card class="mb-6">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  :label="$t('common.search') || 'Іздеу'"
                  prepend-inner-icon="mdi-magnify"
                  :placeholder="$t('student.instructors.searchPlaceholder') || 'Инструктор атын енгізіңіз'"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @input="filterInstructors"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="ratingFilter"
                  :items="ratingOptions"
                  :label="$t('common.rating') || 'Рейтинг'"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:model-value="filterInstructors"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  :label="$t('common.sortBy') || 'Сұрыптау'"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:model-value="filterInstructors"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-btn
                  variant="outlined"
                  block
                  @click="clearFilters"
                >
                  {{ $t('common.clearFilters') || 'Тазалау' }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Instructors Grid -->
        <v-row v-if="!loading && filteredInstructors.length > 0">
          <v-col
            v-for="instructor in filteredInstructors"
            :key="instructor.id"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card
              class="instructor-card"
              :class="{ 'current-instructor': currentInstructor && currentInstructor.id === instructor.id }"
              height="100%"
              @click="viewInstructorDetails(instructor)"
            >
              <div class="pa-4">
                <!-- Instructor Photo and Basic Info -->
                <div class="d-flex align-center mb-3">
                  <v-avatar size="60" class="me-3">
                    <v-img v-if="instructor.photo" :src="getInstructorPhoto(instructor.photo)" />
                    <v-icon v-else size="30">mdi-account-tie</v-icon>
                  </v-avatar>
                  
                  <div class="flex-grow-1">
                    <h4 class="text-h6 text-truncate">{{ instructor.fio }}</h4>
                    <div class="text-body-2 text-medium-emphasis">
                      <v-icon size="small" class="me-1">mdi-car</v-icon>
                      {{ instructor.carModel }}
                    </div>
                  </div>

                  <v-chip
                    v-if="currentInstructor && currentInstructor.id === instructor.id"
                    color="primary"
                    size="small"
                    variant="tonal"
                  >
                    {{ $t('student.instructors.current') || 'Менікі' }}
                  </v-chip>
                </div>

                <!-- Rating -->
                <div class="d-flex align-center mb-3">
                  <v-rating
                    :model-value="instructor.rating"
                    readonly
                    color="amber"
                    size="small"
                    density="compact"
                  />
                  <span class="text-body-2 ml-2">{{ instructor.rating.toFixed(1) }}</span>
                  <span class="text-caption ml-1">({{ instructor.totalRatings }})</span>
                </div>

                <!-- Description -->
                <p class="text-body-2 text-medium-emphasis mb-3" style="height: 40px; overflow: hidden;">
                  {{ instructor.description || $t('student.instructors.noDescription') || 'Сипаттама жоқ' }}
                </p>

                <!-- Addresses -->
                <div v-if="getInstructorAddresses(instructor).length" class="mb-3">
                  <div class="text-caption text-medium-emphasis mb-1">
                    {{ $t('instructor.addresses') || 'Мекенжайлар' }}
                  </div>
                  <div class="d-flex flex-wrap ga-1">
                    <v-chip
                      v-for="address in getInstructorAddresses(instructor)"
                      :key="address.id || address.address || address.name"
                      size="x-small"
                      variant="tonal"
                      color="teal"
                    >
                      <v-icon start size="x-small">mdi-map-marker</v-icon>
                      {{ address.address || address.name }}
                    </v-chip>
                  </div>
                </div>

                <!-- Free Days and Time -->
                <div class="mb-3">
                  <div class="text-caption text-medium-emphasis mb-1">
                    {{ $t('student.instructors.freeDays') || 'Жұмыс күндері:' }}
                  </div>
                  <div class="d-flex flex-wrap ga-1">
                    <v-chip
                      v-for="day in getFreeDays(instructor)"
                      :key="day"
                      size="x-small"
                      variant="outlined"
                    >
                      {{ day }}
                    </v-chip>
                  </div>
                </div>

                <div class="mb-4">
                  <div class="text-caption text-medium-emphasis mb-1">
                    {{ $t('student.instructors.freeTime') || 'Жұмыс уақыты:' }}
                  </div>
                  <div class="d-flex flex-wrap ga-1">
                    <v-chip
                      v-for="time in getFreeTime(instructor)"
                      :key="time"
                      size="x-small"
                      variant="tonal"
                      color="info"
                    >
                      {{ time }}
                    </v-chip>
                  </div>
                </div>

                <!-- Car Photo (if available) -->
                <v-img
                  v-if="instructor.carPhoto"
                  :src="getInstructorPhoto(instructor.carPhoto)"
                  aspect-ratio="16/9"
                  cover
                  rounded="lg"
                  class="mb-3"
                />
              </div>

              <!-- Action Buttons -->
              <v-card-actions class="pa-4 pt-0">
                <v-btn
                  v-if="!currentInstructor || currentInstructor.id !== instructor.id"
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-account-plus"
                  block
                  @click.stop="selectInstructor(instructor)"
                  :loading="selectingInstructor === instructor.id"
                >
                  {{ $t('student.instructors.select') || 'Таңдау' }}
                </v-btn>

                <v-btn-group v-else variant="outlined" divided class="w-100">
                  <v-btn
                    v-if="features.chat"
                    prepend-icon="mdi-message"
                    @click.stop="openChat(instructor)"
                    class="flex-grow-1"
                  >
                    {{ $t('common.message') || 'Хат' }}
                  </v-btn>
                  <v-btn
                    prepend-icon="mdi-calendar-plus"
                    @click.stop="scheduleLesson(instructor)"
                    class="flex-grow-1"
                  >
                    {{ $t('student.scheduleLesson') || 'Сабақ' }}
                  </v-btn>
                </v-btn-group>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Loading State -->
        <div v-else-if="loading" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-4">{{ $t('common.loading') || 'Жүктелуде...' }}</div>
        </div>

        <!-- Empty State -->
        <v-card v-else class="text-center py-12">
          <v-card-text>
            <v-icon size="80" color="grey-lighten-2" class="mb-4">mdi-account-tie-outline</v-icon>
            <h3 class="text-h6 mb-2">{{ $t('student.instructors.noInstructors') || 'Инструкторлар табылмады' }}</h3>
            <p class="text-body-2 text-medium-emphasis">
              {{ $t('student.instructors.noInstructorsDesc') || 'Іздеу талаптарына сай инструкторлар жоқ' }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Pagination -->
        <div v-if="filteredInstructors.length > itemsPerPage" class="d-flex justify-center mt-6">
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            @update:model-value="scrollToTop"
          />
        </div>
      </v-col>
    </v-row>

    <!-- Instructor Details Dialog -->
    <v-dialog v-model="showInstructorDialog" max-width="800px">
      <v-card v-if="selectedInstructor">
        <v-card-title class="d-flex align-center">
          <v-avatar size="50" class="me-3">
            <v-img v-if="selectedInstructor.photo" :src="getInstructorPhoto(selectedInstructor.photo)" />
            <v-icon v-else>mdi-account-tie</v-icon>
          </v-avatar>
          {{ selectedInstructor.fio }}
        </v-card-title>

        <v-divider />

        <v-card-text>
          <v-row>
            <!-- Basic Info -->
            <v-col cols="12" md="6">
              <div class="mb-4">
                <div class="text-subtitle-1 mb-2">{{ $t('common.basicInfo') || 'Негізгі ақпарат' }}</div>
                
                <div class="mb-2">
                  <v-icon size="small" class="me-2">mdi-car</v-icon>
                  <span class="text-body-2">{{ selectedInstructor.carModel }}</span>
                </div>

                <div class="d-flex align-center mb-2">
                  <v-icon size="small" class="me-2">mdi-star</v-icon>
                  <v-rating
                    :model-value="selectedInstructor.rating"
                    readonly
                    color="amber"
                    size="small"
                    density="compact"
                  />
                  <span class="text-body-2 ml-2">{{ selectedInstructor.rating.toFixed(1) }}</span>
                  <span class="text-caption ml-1">({{ selectedInstructor.totalRatings }} {{ $t('common.reviews') || 'пікір' }})</span>
                </div>

                <div v-if="selectedInstructor.description" class="mt-3">
                  <div class="text-caption text-medium-emphasis mb-1">{{ $t('common.description') || 'Сипаттама' }}</div>
                  <p class="text-body-2">{{ selectedInstructor.description }}</p>
                </div>
              </div>
            </v-col>

            <v-col v-if="getInstructorAddresses(selectedInstructor).length" cols="12">
              <div class="text-subtitle-1 mb-2">{{ $t('instructor.addresses') || 'Мекенжайлар' }}</div>
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="address in getInstructorAddresses(selectedInstructor)"
                  :key="address.id || address.address || address.name"
                  size="small"
                  variant="tonal"
                  color="teal"
                >
                  <v-icon start size="x-small">mdi-map-marker</v-icon>
                  {{ address.address || address.name }}
                </v-chip>
              </div>
            </v-col>

            <!-- Schedule Info -->
            <v-col cols="12" md="6">
              <div class="mb-4">
                <div class="text-subtitle-1 mb-2">{{ $t('common.schedule') || 'Кесте' }}</div>
                
                <div class="mb-3">
                  <div class="text-caption text-medium-emphasis mb-1">{{ $t('student.instructors.freeDays') || 'Жұмыс күндері' }}</div>
                  <div class="d-flex flex-wrap ga-1">
                    <v-chip
                      v-for="day in getFreeDays(selectedInstructor)"
                      :key="day"
                      size="small"
                      variant="outlined"
                    >
                      {{ day }}
                    </v-chip>
                  </div>
                </div>

                <div>
                  <div class="text-caption text-medium-emphasis mb-1">{{ $t('student.instructors.freeTime') || 'Жұмыс уақыты' }}</div>
                  <div class="d-flex flex-wrap ga-1">
                    <v-chip
                      v-for="time in getFreeTime(selectedInstructor)"
                      :key="time"
                      size="small"
                      variant="tonal"
                      color="info"
                    >
                      {{ time }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Car Photo -->
            <v-col v-if="selectedInstructor.carPhoto" cols="12">
              <div class="text-subtitle-1 mb-2">{{ $t('student.instructors.carPhoto') || 'Көлік суреті' }}</div>
              <v-img
                :src="getInstructorPhoto(selectedInstructor.carPhoto)"
                aspect-ratio="16/9"
                cover
                rounded="lg"
                class="mx-auto"
                max-width="400"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="showInstructorDialog = false">{{ $t('common.close') || 'Жабу' }}</v-btn>
          
          <v-btn
            v-if="!currentInstructor || currentInstructor.id !== selectedInstructor.id"
            color="primary"
            @click="selectInstructor(selectedInstructor)"
            :loading="selectingInstructor === selectedInstructor.id"
          >
            {{ $t('student.instructors.select') || 'Таңдау' }}
          </v-btn>

          <v-btn
            v-else
            color="primary"
            @click="scheduleLesson(selectedInstructor)"
          >
            {{ $t('student.scheduleLesson') || 'Сабақ жазылу' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Messages -->
    <v-snackbar v-model="showSuccess" color="success">{{ successMessage }}</v-snackbar>
    <v-snackbar v-model="showError" color="error">{{ errorMessage }}</v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { features } from '@/config/features'
import { useAuthStore } from '@/stores/auth'
import api from '@/utils/api'

// Composables
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()

// State
const loading = ref(false)
const instructors = ref([])
const filteredInstructors = ref([])
const currentInstructor = ref(null)
const selectedInstructor = ref(null)
const selectingInstructor = ref(null)
const showInstructorDialog = ref(false)
const showSuccess = ref(false)
const showError = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Filters
const search = ref('')
const ratingFilter = ref('all')
const sortBy = ref('rating')
const currentPage = ref(1)
const itemsPerPage = ref(9)

// Options
const ratingOptions = [
  { title: t('common.all') || 'Барлығы', value: 'all' },
  { title: '4.5+ ⭐', value: '4.5' },
  { title: '4.0+ ⭐', value: '4.0' },
  { title: '3.5+ ⭐', value: '3.5' },
  { title: '3.0+ ⭐', value: '3.0' }
]

const sortOptions = [
  { title: t('common.rating') || 'Рейтинг бойынша', value: 'rating' },
  { title: t('common.name') || 'Атауы бойынша', value: 'name' },
  { title: t('student.instructors.experience') || 'Тәжірибе бойынша', value: 'experience' }
]

// Computed
const totalPages = computed(() => {
  return Math.ceil(filteredInstructors.value.length / itemsPerPage.value)
})

const paginatedInstructors = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredInstructors.value.slice(start, end)
})

// Methods
const loadInstructors = async () => {
  loading.value = true
  try {
    const response = await api.get('/instructors')
    instructors.value = response.data || []
    
    // Load current instructor if student has one assigned
    if (authStore.user.instructorId) {
      const currentResponse = await api.get(`/instructors/${authStore.user.instructorId}`)
      currentInstructor.value = currentResponse.data
    }
    
    filterInstructors()
  } catch (error) {
    // Error
    showErrorMessage(error.response?.data?.error || 'Инструкторларды жүктеу қатесі')
  } finally {
    loading.value = false
  }
}

const filterInstructors = () => {
  let filtered = [...instructors.value]

  // Search filter
  if (search.value) {
    const searchTerm = search.value.toLowerCase()
    filtered = filtered.filter(instructor =>
      instructor.fio.toLowerCase().includes(searchTerm) ||
      instructor.carModel.toLowerCase().includes(searchTerm) ||
      (instructor.description && instructor.description.toLowerCase().includes(searchTerm))
    )
  }

  // Rating filter
  if (ratingFilter.value !== 'all') {
    const minRating = parseFloat(ratingFilter.value)
    filtered = filtered.filter(instructor => instructor.rating >= minRating)
  }

  // Sort
  switch (sortBy.value) {
    case 'rating':
      filtered.sort((a, b) => b.rating - a.rating)
      break
    case 'name':
      filtered.sort((a, b) => a.fio.localeCompare(b.fio))
      break
    case 'experience':
      filtered.sort((a, b) => (b.totalRatings || 0) - (a.totalRatings || 0))
      break
  }

  filteredInstructors.value = filtered
  currentPage.value = 1
}

const clearFilters = () => {
  search.value = ''
  ratingFilter.value = 'all'
  sortBy.value = 'rating'
  filterInstructors()
}

const selectInstructor = async (instructor) => {
  selectingInstructor.value = instructor.id
  try {
    await api.put('/user/profile', {
      instructorId: instructor.id
    })
    
    currentInstructor.value = instructor
    authStore.user.instructorId = instructor.id
    
    showSuccessMessage(`${instructor.fio} сәтті таңдалды!`)
    showInstructorDialog.value = false
  } catch (error) {
    // Error
    showErrorMessage(error.response?.data?.error || 'Инструкторды таңдау қатесі')
  } finally {
    selectingInstructor.value = null
  }
}

const viewInstructorDetails = (instructor) => {
  selectedInstructor.value = instructor
  showInstructorDialog.value = true
}

const openChat = (instructor) => {
  // Navigate to chat with instructor
  router.push(`/student/chat/${instructor.id}`)
}

const scheduleLesson = (instructor) => {
  // Navigate to schedule page
  router.push('/student/schedule')
}

const getInstructorPhoto = (photo) => {
  if (!photo) return null
  
  const sanitizedPath = photo.replace(/^\/+/, '')
  
  // Use API endpoint for images
  if (sanitizedPath.startsWith('instructors/')) {
    return `/api/images/${sanitizedPath}`
  }
  return `/api/images/instructors/${sanitizedPath}`
}

const getFreeDays = (instructor) => {
  try {
    if (Array.isArray(instructor.freeDays)) return instructor.freeDays
    return JSON.parse(instructor.freeDays || '[]')
  } catch {
    return []
  }
}

const getFreeTime = (instructor) => {
  try {
    if (Array.isArray(instructor.freeTime)) return instructor.freeTime
    return JSON.parse(instructor.freeTime || '[]')
  } catch {
    return []
  }
}

const getInstructorAddresses = (instructor) => {
  if (!instructor) return []
  if (Array.isArray(instructor.addresses) && instructor.addresses.length) return instructor.addresses
  return instructor.address ? [instructor.address] : []
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const showSuccessMessage = (message) => {
  successMessage.value = message
  showSuccess.value = true
}

const showErrorMessage = (message) => {
  errorMessage.value = message
  showError.value = true
}

// Lifecycle
onMounted(() => {
  loadInstructors()
})
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(45deg, #FFC107, #FF9800);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.instructor-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.instructor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.current-instructor {
  border: 2px solid rgb(var(--v-theme-primary));
}

.current-instructor:before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 50px 50px 0;
  border-color: transparent rgb(var(--v-theme-primary)) transparent transparent;
}
</style>
